import React, { useRef } from 'react';
import type { MutableRefObject } from 'react';
import {
  Card, Form, Input, Select, DatePicker, Button, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';

import useSmartBack from '@hooks/shared/useSmartBack';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import PageHeader from '@shared/PageHeader';
import OrderItemsCard from '@components/shared/orders/OrderItemsCard';
import { ITEM_MODE_PRECISE } from '@hooks/orders/useItemMode';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { inventoryService as adminInventoryService } from '@services/admin/inventoryService';
import { inventoryService as operatorInventoryService } from '@services/operator/inventoryService';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

const HOLD_REASONS = ['customer_reserve', 'quality_issue', 'regulatory', 'damage', 'recall', 'other'] as const;
type HoldReason = typeof HOLD_REASONS[number];

type HoldRole = 'admin' | 'operator';

interface InventoryTypeOption {
  id: string;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

// Local interface for the service prop. The actual service is fully typed at its
// own boundary (Tasks 2-3 added Zod parsing). This describes just the surface
// this component reads from.
interface InventoryHoldServiceLike {
  create(payload: Record<string, unknown>): Promise<
    | { success: true; message: string; data: { id: string; number?: string }; [k: string]: unknown }
    | { success: false; message: string; errors?: Record<string, unknown>; [k: string]: unknown }
  >;
}

interface InventoryHoldNewProps {
  role: HoldRole;
  service: InventoryHoldServiceLike;
  productService: unknown; // pass-through to OrderItemsCard; OrderItemsForm types this as unknown
  inventoryTypes: InventoryTypeOption[];
  merchantId?: string;          // operator must pass; admin uses MerchantContext upstream
  defaultWarehouseId?: string;  // admin only; operator backend uses current_warehouse
}

/**
 * Narrow view of the form-row shape — buildPayload only reads inventory_id +
 * quantity. The actual runtime row written by OrderItemsForm has more fields
 * (product_id, _source_inventory, batch, etc.); we drop them on submit.
 */
interface HoldOrderItem {
  inventory_id: string;
  quantity: number;
}

interface HoldFormValues {
  reason: HoldReason;
  expires_on?: Dayjs | null;
  note?: string;
  order_items: HoldOrderItem[];
}

const InventoryHoldNew: React.FC<InventoryHoldNewProps> = ({
  role,
  service,
  productService,
  inventoryTypes,
  merchantId,
  defaultWarehouseId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const [form] = Form.useForm<HoldFormValues>();

  const [loading, setLoading] = React.useState(false);
  const [savingAndAdding, setSavingAndAdding] = React.useState(false);

  // OrderItemsCard writes Form.List's `add` / `remove` into these refs.
  // addItemRef is invoked by handleAddItem (footer button); removeItemRef is unused.
  const addItemRef: MutableRefObject<((item: Record<string, unknown>) => void) | null> = useRef(null);
  const removeItemRef: MutableRefObject<((index: number | number[]) => void) | null> = useRef(null);

  const inventoryAttributeDefinitions = useInventoryAttributeDefinitions(role, merchantId);

  // Append an empty row to order_items. OrderItemsCard sets addItemRef.current
  // on first render, so this is safe to call from a button onClick.
  const handleAddItem = () => {
    addItemRef.current?.({
      product_id: null,
      inventory_id: null,
      quantity: 1,
    });
  };

  // Build submit payload from validated form values.
  // No fallback patterns: every field is type-guaranteed by validation rules.
  //   - reason: required by Form.Item rule
  //   - order_items: required + non-empty by OrderItemsCard `required` validator
  //   - inventory_id / quantity per row: required by ProductItemCard's Form.Item rules
  // Only genuinely user-optional fields: expires_on (DatePicker) and note (TextArea).
  const buildPayload = (values: HoldFormValues): Record<string, unknown> => {
    return {
      reason: values.reason,
      expires_on: values.expires_on?.format('YYYY-MM-DD') ?? null,
      note: values.note,
      ...(role === 'operator' && merchantId ? { merchant_id: merchantId } : {}),
      items: values.order_items.map((i): HoldOrderItem => ({
        inventory_id: i.inventory_id,
        quantity: i.quantity,
      })),
    };
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      try {
        const response = await service.create(buildPayload(values));
        if (response.success) {
          message.success(t('common.message.create_success') as string);
          // Backend contract: success returns data.id. If missing, runtime throw exposes a real backend bug.
          navigate(`/inventory_holds/${response.data.id}`, { replace: true });
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.create_failed') as string });
        }
      } catch (error) {
        console.error('Failed to create inventory hold:', error);
        handleNetworkError(error, { t });
      } finally {
        setLoading(false);
      }
    } catch (error) {
      // validateFields rejected → antd auto-highlights; scroll + toast hint.
      const errFields = (error as { errorFields?: Array<{ name: unknown }> })?.errorFields;
      if (errFields && errFields.length > 0) {
        message.error(t('common.message.please_check_form') as string);
        form.scrollToField(errFields[0].name as never, { behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await service.create(buildPayload(values));
        if (response.success) {
          message.success(t('common.message.create_success') as string);
          form.resetFields();
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.create_failed') as string });
        }
      } catch (error) {
        console.error('Failed to create inventory hold:', error);
        handleNetworkError(error, { t });
      } finally {
        setSavingAndAdding(false);
      }
    } catch (error) {
      const errFields = (error as { errorFields?: Array<{ name: unknown }> })?.errorFields;
      if (errFields && errFields.length > 0) {
        message.error(t('common.message.please_check_form') as string);
        form.scrollToField(errFields[0].name as never, { behavior: 'smooth', block: 'center' });
      }
    }
  };

  const activeInventoryService = role === 'admin' ? adminInventoryService : operatorInventoryService;
  const warehouseId = role === 'admin' ? defaultWarehouseId : null;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader
        basePath={`/${role}`}
        items={['stocks', 'stocks.inventory_holds', { label: t('shared.inventory_holds.new_title') }]}
      />

      <Form form={form} initialValues={{ reason: 'customer_reserve' }}>
        <Card title={t('shared.inventory_holds.basic_info')} bordered={false} style={{ marginBottom: 16 }}>
          <Form.Item
            label={t('models.inventory_hold.reason')}
            name="reason"
            rules={[{ required: true, message: t('common.validation.required') as string }]}
          >
            <Select style={{ width: 240 }} placeholder={t('common.placeholder.select') as string}>
              {HOLD_REASONS.map(reason => (
                <Select.Option key={reason} value={reason}>
                  {t(`enums.inventory_hold.reason.${reason}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={t('models.inventory_hold.expires_on')} name="expires_on">
            <DatePicker
              placeholder={t('common.placeholder.select_date') as string}
              disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
            />
          </Form.Item>
          <Form.Item label={t('models.inventory_hold.note')} name="note">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Card>

        {/*
          OrderItemsCard hard-codes Form.List name="order_items". Reusing that
          internal field name avoids threading a listName prop through
          OrderItemsCard / OrderItemsForm / ProductItemCard (~1700 LoC).
          The payload mapping (buildPayload above) renames to `items` on submit.
        */}
        <OrderItemsCard
          itemMode={ITEM_MODE_PRECISE}
          onModeChange={() => { /* no-op: toggle hidden */ }}
          showModeToggle={false}
          required
          addItemRef={addItemRef}
          removeItemRef={removeItemRef}
          form={form}
          productService={productService}
          inventoryTypes={inventoryTypes}
          inventoryAttributeDefinitions={inventoryAttributeDefinitions}
          merchantId={merchantId}
          searchMerchantId={merchantId}
          enabledColumns={[]}
          availableColumns={[]}
          showSettings={false}
          role={role}
          inventoryService={activeInventoryService}
          warehouseId={warehouseId}
          multiSelectInventory
        />

        <FloatingActionBarSpacer />
        <FloatingActionBar
          role={role}
          onCancel={goBack}
          onSubmit={handleSubmit}
          loading={loading}
          disabled={savingAndAdding}
          extraButtons={(
            <>
              <Button
                icon={<PlusOutlined />}
                disabled={loading || savingAndAdding}
                onClick={handleAddItem}
              >
                {t('shared.orders.form.add_item')}
              </Button>
              <Button
                loading={savingAndAdding}
                disabled={loading}
                onClick={handleSaveAndAddAnother}
              >
                {t('common.button.save_and_add_another')}
              </Button>
            </>
          )}
          submitText={t('common.button.submit')}
        />
      </Form>
    </div>
  );
};

export default InventoryHoldNew;
